import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import axios from 'axios';
import {toast} from 'react-toastify';
import ApiUtils from '../../../../utils/apiUtils';
import base_url from '../../../../baseUrls';
import {COLORS} from '../../../../styles/styleConstants';
import {CustomInput, FloatingLabel} from '../../../../pieces/inputs/customInputComponent';

const digitsOf = (value) => String(value || '').replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');

/** `YYYY-MM-DD` for the day after today, as a starting point for the custom picker. */
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * The phone the next call should go to. After a `phonenumberchange` the homeowner's new number is
 * in `answer`; otherwise it is the number on the call.
 */
const suggestedPhone = (call) => {
  if (call?.result === 'phonenumberchange' && digitsOf(call.answer).length === 10) {
    return digitsOf(call.answer);
  }
  return call?.phone || '';
};

/**
 * Queues the next attempt for a Call Exceptions call: on the company's callback policy, right
 * now, or at a time the agent picks — optionally to a corrected phone number.
 *
 * The picked time is in the homeowner's zone, not the agent's; agents work from other zones and
 * a callback time only means something where the homeowner is.
 */
const RescheduleCallDialog = ({open, call, onClose}) => {
  const [when, setWhen] = useState('policy');
  const [date, setDate] = useState(tomorrow());
  const [time, setTime] = useState('10:00');
  const [phone, setPhone] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !call?.id) return undefined;
    setWhen('policy');
    setDate(tomorrow());
    setTime('10:00');
    setPhone(suggestedPhone(call));
    setPreview(null);
    setPreviewError('');
    setError('');

    let cancelled = false;
    (async () => {
      try {
        const authToken = await ApiUtils.getAccessToken();
        const response = await axios.post(
          `${base_url.api}ai/reschedule-call`,
          {aiCallId: call.id, when: 'policy', dryRun: true},
          {headers: {Authorization: authToken}},
        );
        if (!cancelled) setPreview(response?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(err?.response?.data?.message ?? 'Could not work out the policy date.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, call]);

  const phoneChanged = digitsOf(phone) !== digitsOf(call?.phone);
  const phoneValid = digitsOf(phone).length === 10;

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.post(
        `${base_url.api}ai/reschedule-call`,
        {
          aiCallId: call.id,
          when,
          ...(when === 'custom' && {scheduledLocal: `${date}T${time}`}),
          ...(phoneChanged && {phoneNumber: digitsOf(phone)}),
        },
        {headers: {Authorization: authToken}},
      );
      const data = response?.data?.data || {};
      toast(`Call queued for ${data.rescheduledForLocal || 'the chosen time'}.`, {type: 'success'});
      onClose(true);
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Could not reschedule this call.');
    } finally {
      setSaving(false);
    }
  };

  const stateLabel = call?.stateName ? ` (${call.stateName})` : '';

  return (
    <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth={'sm'} fullWidth>
      <DialogTitle sx={{fontWeight: 700}}>
        {`Reschedule call${call?.jobNumber ? ` — job #${call.jobNumber}` : ''}`}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{color: COLORS.fontPale, fontSize: '0.85rem', mb: 1}}>
          {`${`${call?.first_name || ''} ${call?.last_name || ''}`.trim() || 'Homeowner'}`}
          {call?.attempt ? ` · ${call.attempt} attempt${call.attempt === 1 ? '' : 's'} so far` : ''}
        </Typography>

        <RadioGroup value={when} onChange={(e) => setWhen(e.target.value)}>
          <FormControlLabel
            value={'policy'}
            control={<Radio size={'small'}/>}
            label={
              <Box>
                <Typography>Company policy</Typography>
                <Typography sx={{color: COLORS.fontPale, fontSize: '0.8rem'}}>
                  {preview
                    ? `${preview.rescheduledForLocal} — ${preview.callbackDays} day${
                      preview.callbackDays === 1 ? '' : 's'} from today`
                    : previewError || 'Working out the date...'}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value={'now'}
            control={<Radio size={'small'}/>}
            label={
              <Box>
                <Typography>Call now</Typography>
                <Typography sx={{color: COLORS.fontPale, fontSize: '0.8rem'}}>
                  Goes out on the next dialer run, ahead of the normal queue.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value={'custom'}
            control={<Radio size={'small'}/>}
            label={<Typography>Pick a date and time</Typography>}
          />
        </RadioGroup>

        {/* MUI TextField labels collide with the theme's shrunk-label override and sit on top of
            the value; the portal's floating-label input is the pattern that renders correctly. */}
        {when === 'custom' && (
          <Box sx={{display: 'flex', gap: 1.5, ml: 4, mt: 1.5, flexWrap: 'wrap'}}>
            <FormControl variant={'standard'}>
              <FloatingLabel apiName={'reschedule-call-date'} label={'Date'} />
              <CustomInput
                id={'reschedule-call-date'}
                type={'date'}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </FormControl>
            <FormControl variant={'standard'}>
              <FloatingLabel apiName={'reschedule-call-time'} label={`Homeowner's time${stateLabel}`} />
              <CustomInput
                id={'reschedule-call-time'}
                type={'time'}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                inputProps={{step: 300}}
              />
            </FormControl>
          </Box>
        )}

        <FormControl variant={'standard'} fullWidth error={!phoneValid} sx={{mt: 3}}>
          <FloatingLabel apiName={'reschedule-call-phone'} label={'Phone to call'} />
          <CustomInput
            id={'reschedule-call-phone'}
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={!phoneValid}
          />
          <FormHelperText sx={{mx: 0}}>
            {!phoneValid
              ? 'Enter a 10-digit US phone number.'
              : phoneChanged
                ? `The new call goes to this number instead of ${call?.phone || 'the old one'}.`
                : ' '}
          </FormHelperText>
        </FormControl>

        {error && (
          <Typography color={'error'} sx={{mt: 1, fontSize: '0.9rem'}}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button onClick={() => onClose(false)} disabled={saving}>Cancel</Button>
        <Button
          variant={'contained'}
          onClick={submit}
          disabled={saving || !phoneValid || (when === 'custom' && (!date || !time))}
        >
          {saving ? 'Scheduling...' : 'Schedule call'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RescheduleCallDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  call: PropTypes.instanceOf(Object),
  onClose: PropTypes.func.isRequired,
};

RescheduleCallDialog.defaultProps = {
  call: null,
};

export default RescheduleCallDialog;
